import React, { useCallback, useEffect } from 'react';
import './Sources.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { numeralFormatterCurrency } from 'ns_libs/formatter';
import { NSButton, NSCard } from 'bricks';
import { FormikErrors } from 'formik';
import { DEVELOPMENT_SOURCE_METRIC } from 'constants/sources';
import RenderIf from 'components/RenderIf/RenderIf';
import BOEDiscrepancyIcon from 'views/ProFormaTable/components/BOEDiscrepancyIcon/BOEDiscrepancyIcon';
import { DEVELOPMENT_EXPANDABLE_SECTIONS } from 'views/ProFormaTable/constants';
import { INTEREST_RESERVE_WIZARD_TEXT } from 'views/ProFormaWizard/constants';
import { faLightbulbGear } from '@fortawesome/pro-solid-svg-icons';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import Source from './Source/Source';
import { ISource, ISourcesProps, SourceField } from './Source/types';
import { sourceTypeOptions } from './Source/constants';
import {
    addNewSource, createTraunchOptions, removeSourceById, updateSourceField,
} from './helpers';

const Sources = ({
    totalUses, totalSources, sources, onChange, errors, touched, onBlur,
    boeDiscrepancies,
}: ISourcesProps) => {
    const traunchOptions = createTraunchOptions(sources.length);

    const handleChangeSource = useCallback(
        (id: string, field: SourceField, value: any) => {
            const updatedSources = updateSourceField(sources, id, field, value, totalUses);
            onChange(updatedSources);
        },
        [sources, totalUses, onChange],
    );

    const handleRemoveSource = useCallback(
        (id: string) => {
            const updatedSources = removeSourceById(sources, id);
            onChange(updatedSources);
        },
        [sources, onChange],
    );

    const handleAddSource = useCallback(() => {
        const updatedSources = addNewSource(sources);
        onChange(updatedSources);
    }, [sources, onChange]);

    const handleSourcesBlur = (id: string, field: SourceField) => {
        const sourceIndex = sources.findIndex(source => source.id === id);
        if (sourceIndex > -1) {
            onBlur(sourceIndex, field);
        }
    };

    useEffect(() => {
        sources.map(source => {
            if (source.metricType === DEVELOPMENT_SOURCE_METRIC.LTC && source.calculateDebt) {
                const updatedSources = updateSourceField(
                    sources, source.id, 'loanToCost', source.loanToCost, totalUses,
                );
                onChange(updatedSources);
            }
            return null;
        });
    }, [totalUses]);

    return (
        <div className="Sources">
            <div className="d-flex align-items-center my-2">
                <div>
                    <div>2 of 2</div>
                    <div className="d-flex">
                        <div>
                            <div className="Sources__title text-white my-0 font-weight-bold">Sources</div>
                            <div className="Sources__subtitle">Add any information about this investment's sources of funds</div>
                        </div>
                        <RenderIf isTrue={boeDiscrepancies ? !!boeDiscrepancies[DEVELOPMENT_EXPANDABLE_SECTIONS[2].number]?.length : false}>
                            <BOEDiscrepancyIcon
                                section={DEVELOPMENT_EXPANDABLE_SECTIONS[2].title}
                                metricNames={boeDiscrepancies ? boeDiscrepancies[DEVELOPMENT_EXPANDABLE_SECTIONS[2].number] : []}
                                className="ml-2 my-auto"
                            />
                        </RenderIf>
                    </div>
                </div>
            </div>

            {sources.map((source, index) => (
                <Source
                    key={source.id}
                    source={source}
                    onChange={handleChangeSource}
                    onRemove={handleRemoveSource}
                    onBlur={handleSourcesBlur}
                    traunchOptions={traunchOptions}
                    typeOptions={sourceTypeOptions}
                    errors={Array.isArray(errors) && typeof errors[index] === 'object' ? (errors[index] as FormikErrors<ISource>) : undefined}
                    touched={Array.isArray(touched) ? touched[index] : undefined}
                    totalUses={totalUses}
                />
            ))}

            <NSButton className="d-flex align-items-center my-2 Sources__add_source" callback={handleAddSource}>
                <FontAwesomeIcon icon={faPlus} />
                <p>Add source</p>
            </NSButton>

            <NSCard className="p-2 mt-1 justify-content-center Sources__total">
                <div className="d-flex justify-content-between align-items-center">
                    <span className="font-weight-bold text-white">Total sources:</span>

                    <div className="d-flex">
                        <IconWithTooltip
                            name="interestReserveIconWizard-totalSources"
                            icon={faLightbulbGear}
                            tooltipText={INTEREST_RESERVE_WIZARD_TEXT}
                            className="my-auto mr-2"
                        />
                        <span className="font-weight-bold text-white">{numeralFormatterCurrency(totalSources)}</span>
                    </div>
                </div>
            </NSCard>
        </div>
    );
};

export default Sources;
